import { useWallets } from 'providers/WalletsProvider'
import { useAuth } from 'providers/AuthProvider'
import { Button, Drawer, Grid, Modal, Slide } from '@mui/material'
import { Link } from 'react-router-dom'
import profilePicture from '/assets/Icon/profile.svg'
import React, { useEffect, useState } from 'react'
import Dot from '/assets/Icon/Dot.svg'
import wallet from '/assets/Icon/WalletOutline.svg'
import trophy from '/assets/Icon/Trophy.svg'
import callout from '/assets/Icon/Callout.svg'
import useExchangeRate from 'hooks/useExchangeRate'
// import useCollection from 'hooks/useCollection' // Now using createCollection from useWallets()
import MoonpayModal from 'components/Modal/MoonpayModal'
import CustomButton from 'components/Button/CustomButton'
import { clearNotifications, getNotifications } from 'utils/notifications'
import bellIcon from '/assets/Icon/Bell.svg'
import WalletCard from 'components/Card/WalletCard'
import { Add } from '@mui/icons-material'
import { lookupWallets } from 'services/api.service'
import Coach from 'components/DataDisplay/Coach'

const UserMenu = () => {
  const { logOut, user } = useAuth()
  const {
    flowUser,
    isConnected,
    flowWUSDCBalance,
    usingMagic,
    usingCrossmint,
    usingMetaMask,
    crossmintWalletBalance,
    crossmintBalanceRefreshing,
    metamaskWalletBalance,
    magicWalletWUSDCBalance,
    lastUsedWallet,
    crossmintWalletAddress,
    metamaskWalletAddress,
    checkAndUpdateUsersRegisteredWallets,
    disconnect,
    userAuth,
    createCollection,
    collection,
    collectionIsLoading: collectionLoading
  } = useWallets()
  // EVM-first identity. Falls back to Flow only for legacy Magic users so
  // their existing display continues to work; new users see Crossmint /
  // MetaMask addresses everywhere.
  const primaryWalletAddress =
    crossmintWalletAddress || metamaskWalletAddress || flowUser?.addr || ''
  const [userName, setUserName] = useState('')
  const [moonpayModal, showMoonpayModal] = React.useState(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [open, setOpen] = useState(false)
  const [notification, setNotification] = useState('')

  const { rate } = useExchangeRate()

  const userBalanceAUD = React.useMemo(() => {
    // Use the balance of whichever wallet type is currently active so the
    // header stays in sync with the sidebar WalletCard.
    const activeBalance = usingCrossmint
      ? crossmintWalletBalance
      : usingMetaMask
      ? metamaskWalletBalance
      : usingMagic
      ? magicWalletWUSDCBalance
      : flowWUSDCBalance

    return Number(Number(activeBalance) * rate).toFixed(2)
  }, [
    usingCrossmint,
    usingMetaMask,
    usingMagic,
    crossmintWalletBalance,
    metamaskWalletBalance,
    magicWalletWUSDCBalance,
    flowWUSDCBalance,
    rate
  ])

  const fetchUserName = async () => {
    // Use the EVM address as primary identity for the wallet-name lookup;
    // legacy Flow address still works (some old Magic users only have one).
    let userNameOrWalletAddress =
      primaryWalletAddress || user?.preferred_username
    if (primaryWalletAddress) {
      const _userName = await lookupWallets([primaryWalletAddress])
      userNameOrWalletAddress =
        _userName[0]?.username ?? _userName[0]?.WalletAddress
    }
    setUserName(userNameOrWalletAddress)
  }

  useEffect(() => {
    fetchUserName()
  }, [user, primaryWalletAddress, isConnected, usingMagic])

  const handleOpenUserMenu = () => {
    fetchUserName()
    checkAndUpdateUsersRegisteredWallets()
    setOpen(true)
  }

  useEffect(() => {
    const handler = (e: Event) => {
      e.stopImmediatePropagation()
      handleOpenUserMenu()
    }
    window.addEventListener('open-user-menu', handler)
    return () => window.removeEventListener('open-user-menu', handler)
  }, [])

  async function createCollectionAsync() {
    try {
      await createCollection()
    } catch (e) {
      // add papertrail(e)
      console.error(e)
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    handleOpenUserMenu()
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const signOut = () => {
    logOut()
    disconnect()
    handleClose()
  }

  const closeMoonpayModal = () => {
    showMoonpayModal(false)
  }

  useEffect(() => {
    setNotification(getNotifications().newPacks ?? '')
  }, [])

  const dismissNotifications = () => {
    clearNotifications()
  }

  const trophyCabinetLinks = [
    {
      label: 'Trophy Cabinet',
      link: '/trophy-cabinet'
    },
    {
      label: 'Collectibles',
      link: '/trophy-cabinet?section=collectibles'
    },
    {
      label: 'Challenges',
      link: 'https://challenges.aflmint.com.au',
      isExternal: true
    },
    {
      label: 'Packs',
      link: '/trophy-cabinet?section=packs'
    },
    {
      label: 'Sets',
      link: '/trophy-cabinet?section=sets'
    },
    {
      label: 'Rewards',
      link: '/trophy-cabinet?section=rewards'
    },
    {
      label: 'Watchlist',
      link: '/trophy-cabinet?section=watchlist'
    },
    {
      label: 'Activity',
      link: '/trade-history'
    },
    {
      label: 'Settings',
      link: '/account'
    }
  ]

  return (
    <>
      <div className="flex h-full justify-end">
        {notification && (
          <img
            src={callout}
            alt=""
            className="absolute right-[28px] top-[52px] z-[1] block w-[160px] xsm:right-[36px] sm:!right-[52px] smd:hidden"
            onClick={() => dismissNotifications()}
          />
        )}
        <div className="relative flex items-center smd:hidden smd:h-[60px]">
          <Link to="/trophy-cabinet" onClick={() => dismissNotifications()}>
            <img
              src={trophy}
              alt=""
              className="mx-2 h-[27px] xsm:h-[31px] sm:mx-4 "
            />
            {notification && (
              <div
                className={`absolute right-1 top-0 flex h-[18px] w-[20px] items-center justify-center bg-blue2 sm:right-3 ${
                  notification?.length > 9
                    ? 'rounded-[8px] px-[12px]'
                    : 'rounded-[10px]'
                }`}
              >
                <p className="text-xs font-bold tracking-normal text-white ">
                  {notification}
                </p>
              </div>
            )}
            {notification && (
              <div
                className={`absolute right-1/2 top-12 flex translate-x-[50%]`}
                onClick={() => dismissNotifications()}
              >
                <Coach>
                  <div className="pb-4 text-[13px] font-bold leading-[18px] tracking-wide text-white">
                    Your new item is now in your Trophy Cabinet
                  </div>
                  <div className="flex w-full justify-end">
                    <CustomButton
                      type="button"
                      model="secondary"
                      size="small"
                      variant="contained"
                      label="Take a look"
                    />
                  </div>
                </Coach>
              </div>
            )}
          </Link>
        </div>
        <Link className="pl-1 pr-3 pt-1" to="/trade-history">
          <img src={bellIcon} />
        </Link>
        <button
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          color="primary"
          className="flex items-center justify-end"
        >
          <img
            src={wallet}
            alt=""
            className="mx-2 hidden h-[27px] xsm:h-[31px] smd:block"
          />
          {!isConnected ? (
            <div className="ml-[-16px] mr-4 flex h-[26px] items-start">
              <img src={Dot} alt="Red dot" className="inline align-top" />
            </div>
          ) : (
            <div className="relative mr-3 hidden font-bold text-white sm:w-auto smd:block">
              <div className="flex items-center gap-1">
                <p className="align-bottom">A$ {userBalanceAUD}</p>
                {crossmintBalanceRefreshing && (
                  <svg
                    className="size-3 animate-spin text-blue-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                )}
              </div>
            </div>
          )}
          <img
            src={profilePicture}
            alt="Profile"
            className="h-2/3 xsm:h-[29px]"
          />
          {!collection && !usingCrossmint && (
            <div className="ml-[-7px] flex h-[26px] items-start">
              <img src={Dot} alt="Red dot" className="inline align-top" />
            </div>
          )}
        </button>
      </div>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          overflow: 'visible',
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            overflowY: 'auto',
            overflowX: 'visible',
            boxShadow: 'none'
          }
        }}
        className="modal-bg"
      >
        <div className="flex">
          <div
            onClick={() => setOpen(false)}
            className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center bg-white"
          >
            <Add
              fontSize="large"
              style={{
                transform: `rotate(45deg)`,
                color: '#000000'
              }}
            />
          </div>
          <div className="relative min-h-screen min-w-[340px] max-w-[340px] overflow-visible bg-black1 p-6 text-center text-white">
            <div className="flex w-full flex-col items-start justify-start">
              <p className="pb-1 text-xl font-bold !leading-[18px]">
                {userName}
              </p>
              <Link
                onClick={() => setOpen(false)}
                to="/account?section=mint-profile"
                className="text-xs font-bold !leading-[18px] text-grey2"
              >
                Edit profile
              </Link>
            </div>
            <div className="flex w-full flex-col items-start gap-y-3 py-6">
              {trophyCabinetLinks.map((link, index) => (
                <div key={index}>
                  {link.isExternal ? (
                    <a href={link.link}>
                      <p
                        onClick={() => setOpen(false)}
                        className={`text-lg font-bold !leading-5 text-grey2 transition-all hover:text-white`}
                      >
                        {link.label}
                      </p>
                    </a>
                  ) : (
                    <Link to={link.link} key={index}>
                      <p
                        onClick={() => setOpen(false)}
                        className={`text-lg font-bold !leading-5 text-grey2 transition-all hover:text-white`}
                      >
                        {link.label}
                      </p>
                    </Link>
                  )}
                </div>
              ))}
            </div>
            <div className="flex w-full flex-col justify-center gap-y-4">
              <div
                className="faq-line"
                style={{ margin: '0rem', width: '100%' }}
              ></div>
              <div className="flex w-full items-center justify-between">
                <p className="text-sm font-bold !leading-4">Connected Wallet</p>
                <Link
                  to="/account?section=manage-wallets"
                  onClick={() => setOpen(false)}
                >
                  <CustomButton
                    label="Manage"
                    model="secondary"
                    variant="contained"
                    className="!py-2 !pl-4 !pr-2"
                    hasEndIcon={true}
                  />
                </Link>
              </div>
              <div>
                <div className="flex w-full flex-col gap-y-3">
                  {isConnected &&
                    !collection &&
                    !collectionLoading &&
                    !usingCrossmint && (
                      <div
                        className="w-full cursor-pointer"
                        onClick={() => createCollectionAsync()}
                      >
                        <p className="text-left text-lg font-bold !leading-5">
                          Activate Trophy Cabinet{' '}
                          <img
                            src={Dot}
                            alt="Red dot"
                            className="inline align-top"
                          />
                        </p>
                      </div>
                    )}
                  <WalletCard
                    walletName={
                      // Magic UI is sunset — never label the dropdown card as
                      // "Magic" even if a legacy user has lastUsedWallet=magic
                      // in localStorage. Default to Crossmint (the current
                      // primary EVM wallet) so the address shown matches the
                      // rest of the UI.
                      lastUsedWallet === 'metamask' ? 'MetaMask' : 'Crossmint'
                    }
                  />
                </div>
              </div>
              <div
                className="faq-line"
                style={{ margin: '0rem', width: '100%' }}
              ></div>
              <div className="flex w-full flex-col items-start gap-y-4 text-left">
                <a
                  href="https://challenges.aflmint.com.au/support/buying-selling"
                  target="_blank"
                  rel="noreferrer"
                >
                  <p className="font-inter text-sm font-bold tracking-wide text-grey2 transition-all hover:text-white">
                    Help Centre
                  </p>
                </a>
                <div onClick={signOut} className="cursor-pointer">
                  <p className="font-inter text-sm font-bold tracking-wide text-grey2 transition-all hover:text-white">
                    Log Out
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
      <MoonpayModal
        open={moonpayModal}
        onClose={closeMoonpayModal}
        mode="funds"
      />
    </>
  )
}

export default UserMenu
